import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von01Maternal } from '@/types/vonData.types';
import { isValidCNP } from '@/utils/cnp';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Von01MaternalFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von01MaternalForm: React.FC<Von01MaternalFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState<string[]>([]);

  // Form fields
  const [motherName, setMotherName] = useState('');
  const [maternalId, setMaternalId] = useState<string>('');
  const [mrn, setMrn] = useState<string>('');
  const [antenatalSteroidsQual, setAntenatalSteroidsQual] = useState<string>('');
  const [prenatalCare, setPrenatalCare] = useState(false);
  const [diedDr, setDiedDr] = useState(false);
  const [modeDelivery, setModeDelivery] = useState<string>('');
  const [maternalHtn, setMaternalHtn] = useState(false);
  const [maternalDiabetes, setMaternalDiabetes] = useState(false);
  const [chorio, setChorio] = useState(false);
  const [antMagnesium, setAntMagnesium] = useState(false);
  const [maternalLupus, setMaternalLupus] = useState(false);
  const [maternalConditionsOther, setMaternalConditionsOther] = useState<string>('');
  const [maternalAge, setMaternalAge] = useState<string>('');
  const [exposureSmoking, setExposureSmoking] = useState(false);
  const [exposureDrugs, setExposureDrugs] = useState(false);
  const [exposureInfection, setExposureInfection] = useState(false);
  const [exposuresOther, setExposuresOther] = useState<string>('');
  const [folateSupplementation, setFolateSupplementation] = useState<string>('');
  const [maternalGest, setMaternalGest] = useState(false);
  const [multipleCount, setMultipleCount] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getMaternal(patientId);

      if (data) {
        // Check if data has ID (existing record) or is auto-filled from patient
        const isExisting = Boolean(data.id);
        setHasExistingData(isExisting);

        // Track auto-filled fields
        if (data._autofilled && Array.isArray(data._autofilled)) {
          setAutoFilledFields(data._autofilled);
        } else {
          setAutoFilledFields([]);
        }

        setMotherName(data.mother_name || '');
        setMaternalId(data.maternal_id || '');
        setMrn(data.mrn || '');
        setAntenatalSteroidsQual(data.antenatal_steroids_qual?.toString() || '');
        setPrenatalCare(Boolean(data.prenatal_care));
        setDiedDr(data.died_dr === true || Number(data.died_dr) === 1);
        setModeDelivery(data.mode_delivery?.toString() || '');
        setMaternalHtn(Boolean(data.maternal_htn));
        setMaternalDiabetes(Boolean(data.maternal_diabetes));
        setChorio(Boolean(data.chorio));
        setAntMagnesium(Boolean(data.ant_magnesium));
        setMaternalLupus(Boolean(data.maternal_lupus));
        setMaternalConditionsOther(data.maternal_conditions_other || '');
        setMaternalAge(data.maternal_age?.toString() || '');
        setExposureSmoking(Boolean(data.exposure_smoking));
        setExposureDrugs(Boolean(data.exposure_drugs));
        setExposureInfection(Boolean(data.exposure_infection));
        setExposuresOther(data.exposures_other || '');
        setFolateSupplementation(data.folate_supplementation?.toString() || '');
        setMaternalGest(Boolean(data.maternal_gest));
        setMultipleCount(data.multiple_count?.toString() || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (readOnly) return;

    if (maternalId && !isValidCNP(maternalId)) {
      toast({
        title: 'Eroare',
        description: 'CNP-ul mamei nu este valid',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);

      const formData: Partial<Von01Maternal> = {
        patient_id: patientId,
        maternal_id: maternalId || undefined,
        // Only include fields that are NOT auto-filled from patient data
        mrn: mrn || undefined,
        antenatal_steroids_qual: antenatalSteroidsQual ? parseInt(antenatalSteroidsQual) : undefined,
        prenatal_care: prenatalCare,
        died_dr: diedDr,
        mode_delivery: modeDelivery ? parseInt(modeDelivery) : undefined,
        maternal_htn: maternalHtn,
        maternal_diabetes: maternalDiabetes,
        chorio: chorio,
        ant_magnesium: antMagnesium,
        maternal_lupus: maternalLupus,
        maternal_conditions_other: maternalConditionsOther,
        maternal_age: maternalAge ? parseInt(maternalAge) : undefined,
        exposure_smoking: exposureSmoking,
        exposure_drugs: exposureDrugs,
        exposure_infection: exposureInfection,
        exposures_other: exposuresOther,
        folate_supplementation: folateSupplementation ? parseInt(folateSupplementation) : undefined,
        maternal_gest: maternalGest,
        multiple_count: multipleCount ? parseInt(multipleCount) : undefined
      };

      if (hasExistingData) {
        await vonDataService.updateMaternal(patientId, formData);
      } else {
        await vonDataService.createMaternal(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 01: Istoric matern</CardTitle>
        <CardDescription>Date materne și perinatale</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Maternal identification */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Identificare maternă</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="motherName">Numele mamei</Label>
                <Input
                  id="motherName"
                  value={motherName}
                  readOnly
                  aria-describedby="motherNameHelp"
                />
                <p id="motherNameHelp" className="text-sm text-muted-foreground">
                  Preluat din datele pacientului. Se modifică în formularul pacientului.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="maternalId">CNP</Label>
                <Input
                  id="maternalId"
                  value={maternalId}
                  onChange={(e) => setMaternalId(e.target.value.replace(/\D/g, '').slice(0, 13))}
                  placeholder="CNP mamă"
                  maxLength={13}
                  inputMode="numeric"
                  disabled={readOnly}
                />
                {maternalId.length === 13 && !isValidCNP(maternalId) && (
                  <p className="text-sm text-destructive">CNP invalid</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mrn">ID foaie observație (MRN)</Label>
                <Input
                  id="mrn"
                  value={mrn}
                  onChange={(e) => setMrn(e.target.value)}
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maternalAge">Vârstă maternă (ani)</Label>
                <Input
                  id="maternalAge"
                  type="number"
                  min={12}
                  max={60}
                  value={maternalAge}
                  onChange={(e) => setMaternalAge(e.target.value)}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Prenatal care */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Îngrijire prenatală</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="prenatalCare"
                  checked={prenatalCare}
                  onCheckedChange={(checked) => setPrenatalCare(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="prenatalCare" className="text-sm font-normal cursor-pointer">
                  Îngrijire prenatală primită
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="steroidsQual">Calitate steroizi antenatali</Label>
                <Select value={antenatalSteroidsQual} onValueChange={setAntenatalSteroidsQual} disabled={readOnly}>
                  <SelectTrigger id="steroidsQual">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Complet</SelectItem>
                    <SelectItem value="2">Parțial</SelectItem>
                    <SelectItem value="3">Nu</SelectItem>
                    <SelectItem value="9">Necunoscut</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Periconceptional folate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="antMagnesium"
                checked={antMagnesium}
                onCheckedChange={(checked) => setAntMagnesium(checked === true)}
                disabled={readOnly}
              />
              <Label htmlFor="antMagnesium" className="text-sm font-normal cursor-pointer">
                Sulfat de magneziu antenatal
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="folateSupplementation">Folat periconcepțional</Label>
              <Select value={folateSupplementation} onValueChange={setFolateSupplementation} disabled={readOnly}>
                <SelectTrigger id="folateSupplementation">
                  <SelectValue placeholder="Selectează..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Da</SelectItem>
                  <SelectItem value="2">Nu</SelectItem>
                  <SelectItem value="3">Necunoscut</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Maternal conditions */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Afecțiuni materne</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="maternalHtn"
                  checked={maternalHtn}
                  onCheckedChange={(checked) => setMaternalHtn(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="maternalHtn" className="text-sm font-normal cursor-pointer">
                  Hipertensiune maternă
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="maternalDiabetes"
                  checked={maternalDiabetes}
                  onCheckedChange={(checked) => setMaternalDiabetes(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="maternalDiabetes" className="text-sm font-normal cursor-pointer">
                  Diabet matern
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="chorio"
                  checked={chorio}
                  onCheckedChange={(checked) => setChorio(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="chorio" className="text-sm font-normal cursor-pointer">
                  Corioamniotită
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="exposureInfection"
                  checked={exposureInfection}
                  onCheckedChange={(checked) => setExposureInfection(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="exposureInfection" className="text-sm font-normal cursor-pointer">
                  Infecții
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="maternalLupus"
                  checked={maternalLupus}
                  onCheckedChange={(checked) => setMaternalLupus(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="maternalLupus" className="text-sm font-normal cursor-pointer">
                  Lupus matern
                </Label>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="maternalConditionsOther">Alte patologii materne</Label>
                <Input
                  id="maternalConditionsOther"
                  value={maternalConditionsOther}
                  onChange={(e) => setMaternalConditionsOther(e.target.value)}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Maternal exposures and folate */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Noxe materne</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="exposureSmoking"
                  checked={exposureSmoking}
                  onCheckedChange={(checked) => setExposureSmoking(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="exposureSmoking" className="text-sm font-normal cursor-pointer">
                  Fumat
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="exposureDrugs"
                  checked={exposureDrugs}
                  onCheckedChange={(checked) => setExposureDrugs(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="exposureDrugs" className="text-sm font-normal cursor-pointer">
                  Droguri
                </Label>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="exposuresOther">Alte substanțe</Label>
                <Input
                  id="exposuresOther"
                  value={exposuresOther}
                  onChange={(e) => setExposuresOther(e.target.value)}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Multiple gestation */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Gestație multiplă</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="maternalGest"
                  checked={maternalGest}
                  onCheckedChange={(checked) => setMaternalGest(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="maternalGest" className="text-sm font-normal cursor-pointer">
                  Gestație multiplă
                </Label>
              </div>

              {maternalGest && (
                <div className="space-y-2">
                  <Label htmlFor="multipleCount">Număr copii</Label>
                  <Select value={multipleCount} onValueChange={setMultipleCount} disabled={readOnly}>
                    <SelectTrigger id="multipleCount">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Doi</SelectItem>
                      <SelectItem value="2">Trei</SelectItem>
                      <SelectItem value="3">Patru</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Mode of delivery */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="modeDelivery">Mod de naștere</Label>
                <Select value={modeDelivery} onValueChange={setModeDelivery} disabled={readOnly}>
                  <SelectTrigger id="modeDelivery">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Vaginală</SelectItem>
                    <SelectItem value="2">Cezariană</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="diedDr"
                  checked={diedDr}
                  onCheckedChange={(checked) => setDiedDr(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="diedDr" className="text-sm font-normal cursor-pointer">
                  Decedată în sala de naștere
                </Label>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
